import React, { useEffect } from 'react';
import { useQuery } from '@apollo/client';
import isNil from 'lodash-es/isNil';
import { gql } from '@yi/core';

import { Episode } from '~/src/common/lean-back/Episode';
import { SeriesEpisodePageContext } from '~/src/common/lean-back/page-contexts';
import { getSeriesTitle } from '~/src/common/lean-back/series-utils';
import { useCurrentUser } from '~/src/common/user';
import { useLocale } from '~/src/components/LocaleProvider';
import { useMediaPlayer } from '~/src/components/MediaPlayer';
import { useMediaSourceDetails } from '~/src/components/MediaPlayer/useMediaSourceDetails';
import {
  PLAYER_TYPE_KEY,
  SERIES_TITLE_KEY,
  useYouboraAnalitics,
} from '~/src/components/MediaPlayer/Youbora';
import { usePageContext } from '~/src/components/PageContextProvider';

export const YouboraAnalytics: React.FC<{
  episode: Episode;
}> = ({ episode }) => {
  const { plugin } = useYouboraAnalitics();
  const { player, mediaId } = useMediaPlayer();
  const mediaDetails = useMediaSourceDetails();
  const { currentUser } = useCurrentUser();
  const { locale } = useLocale();

  const { seriesId } = usePageContext<SeriesEpisodePageContext>();

  const { data } = useQuery<gql.typesClient.Query_Series, gql.typesClient.Query_SeriesVariables>(
    gql.Query_Series,
    {
      variables: { id: seriesId },
      fetchPolicy: 'cache-and-network',
    },
  );
  useEffect(() => {
    if (player !== null && plugin !== null) {
      // See all options https://documentation.npaw.com/integration-docs/docs/options-video-js
      plugin?.setVideoOptions({
        'content.id': mediaId,
        'content.title': mediaDetails?.name,
        'content.duration': mediaDetails?.duration,
        'content.isLive': false,
        'content.language': locale,
        'content.type': gql.typesClient.LogConsumptionTrackingType.SERIES,
        'content.season': episode.seasonNum(),
        'content.episodeTitle': episode.title(),
        [PLAYER_TYPE_KEY]: 'SeriesPlayer',
        [SERIES_TITLE_KEY]: !isNil(data) ? getSeriesTitle(data.Series) : '',
        metadata: {
          episodeId: episode.episodeNum(),
        },
      });
    }
  }, [currentUser, data, episode, locale, mediaDetails, mediaId, player, plugin]);

  return null;
};
